
// The soil legend used to be a single beige swatch reading "See map key",
// pending a soil adviser writing 29 England and Wales class names out by hand.
// It is now built from the soil types in the open field, with colours taken from
// each provider's own legend endpoint: see lib/soilLegend.js.

// England / Wales: unified High/Medium/Low risk ramp. Swatch colours chosen to
// match the rendered EA / NRW risk-map tiles (BBB-457).
//
// The per-nation "how flood risk is assessed" links live in ONE place —
// FLOOD_SOURCE_LINK in aggregateLandSummary.js — and are surfaced to farmers on
// the Zone Details flood row (the map legend renders label + swatches only).
// Band wording confirmed 11/09/2026: High / Medium / Low stands.
export const FLOOD_RISK_LEGEND = {
  label: 'Flood risk',
  swatches: [
    { colour: '#555B9D', label: 'High' },
    { colour: '#9A9FDE', label: 'Medium' },
    { colour: '#C3E0FF', label: 'Low' },
  ],
};

// Scotland: SEPA map shows flood TYPE at medium likelihood, not a risk band.
// Colours pinned from SEPA Flood_Maps WMS Medium_Likelihood layer styles.
export const FLOOD_TYPE_LEGEND_SCOTLAND = {
  // Reads 'Flood risk' to match the toggle above it, the same one-name-per-layer
  // rule that settled protected-sites. Wales' equivalent card already did.
  label: 'Flood risk',
  swatches: [
    { colour: '#00C5FF', label: 'River' },
    { colour: '#4CC900', label: 'Coastal' },
    { colour: '#C500FF', label: 'Surface water' },
  ],
};

// Wales (NRW): flood is served as three separate layers (rivers / sea / surface
// water), each with its own High/Medium/Low ramp — and can't be recoloured (WMS,
// dynamic styling disabled server-side). Option (b), per Davin: one representative
// swatch per flood type + a SINGLE shared "darker = higher risk" caption rather than
// nine swatches. Colours are mid-ramp, matched to the NRW DataMapWales tiles. (BBB-457)
export const FLOOD_TYPE_LEGEND_WALES = {
  label: 'Flood risk',
  caption: 'Darker shade = higher risk',
  swatches: [
    { colour: '#4A90D9', label: 'Rivers' },
    { colour: '#5FB05F', label: 'Sea' },
    { colour: '#B968C7', label: 'Surface water' },
  ],
};

// EVERY sourceLink was status-checked on 20/08/2026 and eight were broken. Four
// were hard 404s (the Scottish NVZ publication, the England ALC guidance, the
// safeguard-zones dataset, and one gov.uk planning-proposals page doing double
// duty for SAC and SPA), two were redirects that had drifted off the subject
// (the England NVZ and SSSI guidance), one landed on a Welsh-language splash
// page (NRW designated sites, fixed with ?lang=en) and one redirected to a
// generic resources index (Hutton's Scottish LCA page). Re-check them rather
// than trusting them: these are other people's URLs and they move.
//
export const LEGEND_METADATA = {
  'nvz-2021-england': {
    label: 'Nitrate Vulnerable Zones',
    accordionSummary:
      'Nitrate Vulnerable Zones (NVZs) are areas where agricultural nitrate can pollute water. Extra rules on fertiliser and manure use apply within these zones. Data: Environment Agency / Defra (OGL).',
    sourceLink: 'https://www.gov.uk/government/collections/nitrate-vulnerable-zones',
    swatches: [
      // fillOpacity mirrors the catalogue override in nitrate.js. England NVZs
      // are held below the designations' default because their area is large and
      // Safeguard draws over the protected sites (BBB-456) — so the swatch has to
      // be held down by the same amount or the key overstates the map.
      // Solid, because the layer is solid. #E600A9 is Defra's own NVZ fill,
      // sampled from a rendered tile on 13/08/2026, and the same magenta the
      // Scottish NVZ renderer uses, so one colour means "nitrate zone" in both
      // nations without either legend contradicting its map.
      //
      // The swatch shows full strength while the map draws at 0.30, and that is the
      // one place fillOpacity is deliberately NOT mirrored. A ~16px swatch at 0.30
      // over a dark panel is barely a colour at all, and the swatch's job is to say
      // which colour to look for, not how faint it will be.
      {
        colour: '#E600A9',
        label: 'Nitrate Vulnerable Zone',
        type: 'solid',
      },
    ],
  },
  'nvz-scotland': {
    label: 'Nitrate Vulnerable Zones',
    accordionSummary:
      'Nitrate Vulnerable Zones (NVZs) are areas where agricultural nitrate can pollute water. Extra rules on fertiliser and manure use apply within these zones. Scotland has five designated NVZs. Data: Scottish Government (OGL).',
    sourceLink: 'https://www.gov.scot/publications/nitrate-vulnerable-zones-guidance-for-farmers/',
    swatches: [
      {
        colour: '#F2D024',
        label: 'Nitrate Vulnerable Zone',
        type: 'outline-tint',
      },
    ],
  },
  'drwpa-england': {
    label: 'Drinking Water Safeguard Zones',
    accordionSummary:
      'Areas draining towards drinking water sources where the use of nitrates and pesticides is closely monitored. England only. Data: Environment Agency (Open Government Licence).',
    // Was a data.gov.uk dataset listing, i.e. a download page. The Environment
    // Agency's own farmer-facing page opens by saying what a Safeguard Zone is
    // and what it means for someone farming in one, then lets them check their
    // land. There is no pure gov.uk explainer for the concept; checked
    // 11/09/2026. Verified 200 the same day.
    sourceLink: 'https://environment.data.gov.uk/farmers/',
    swatches: [
      // Defra's own treatment: a black stipple over white, edged blue-grey. See
      // the NVZ note above for why this is not held down to the map's 0.35.
      {
        colour: '#6B7A8F',
        label: 'Drinking Water Safeguard Zone',
        type: 'stipple',
      },
    ],
  },
  'sssi-england': {
    label: 'Sites of Special Scientific Interest',
    accordionSummary:
      "SSSIs are the main designation for protecting England's most important wildlife and geological sites. Data: Natural England / Environment Agency (OGL).",
    sourceLink:
      'https://www.gov.uk/guidance/sites-of-special-scientific-interest-managing-your-land-and-getting-consent-for-activities',
    swatches: [
      // Swatch pattern mirrors the rendered hatch exactly — slash SSSI /
      // backslash SAC / vertical SPA — so the key cannot contradict the map
      // (BBB-457). The hatch, not just hue, carries the distinction, which is
      // what makes the three tellable apart where they overlap and what a
      // colourblind reader relies on.
      //
      // These were 'outline' / 'dashed' / 'dotted' until 18/08, matching the
      // stroke-only rendering. The map now fills with a pattern, so the swatches
      // had to follow: a key that shows a border for a hatched fill is the exact
      // drift lib/fillOpacity.js exists to prevent.
      {
        colour: '#009E73',
        label: 'SSSI boundary',
        type: 'hatch',
        hatch: 'slash',
      },
    ],
  },
  'sac-england': {
    label: 'Special Areas of Conservation',
    accordionSummary:
      'SACs are strictly protected sites for rare or threatened habitats and species under the Habitats Directive. Data: Natural England / Environment Agency (OGL).',
    sourceLink:
      'https://jncc.gov.uk/our-work/special-areas-of-conservation/',
    swatches: [
      {
        colour: '#CC79A7',
        label: 'SAC boundary',
        type: 'hatch',
        hatch: 'backslash',
      },
    ],
  },
  'spa-england': {
    label: 'Special Protection Areas',
    accordionSummary:
      'SPAs are protected sites for rare and vulnerable wild bird species under the Birds Directive. Data: Natural England / Environment Agency (OGL).',
    sourceLink:
      'https://jncc.gov.uk/our-work/special-protection-areas/',
    swatches: [
      {
        colour: '#E69F00',
        label: 'SPA boundary',
        type: 'hatch',
        // Vertical since 19/08/2026, following the map off cross. This has to
        // move in the same commit as the catalogue or the key starts lying.
        hatch: 'vertical',
      },
    ],
  },
  // Shared by the three split Wales overlays (legendGroup).
  'nrw-designated-sites-wales': {
    label: 'Designated sites',
    sourceLinkLabel: 'protected sites in Wales',
    accordionSummary:
      'Sites of Special Scientific Interest (SSSIs), Special Areas of Conservation (SACs) and Special Protection Areas (SPAs) in Wales. Data: Natural Resources Wales / DataMapWales.',
    sourceLink:
      'https://naturalresources.wales/guidance-and-advice/environmental-topics/wildlife-and-biodiversity/protected-areas-of-land-and-seas/?lang=en',
    swatches: [
      { colour: '#CD6699', label: 'SSSI boundary' },
      { colour: '#0000FF', label: 'SAC boundary' },
      { colour: '#3D7A17', label: 'SPA boundary' },
    ],
  },
  // Scotland designations: map rendering untouched; legend matched to native
  // NatureScot tiles (SSSI red+cross, SAC blue+vertical, SPA purple+horizontal).
  'naturescot-designated-scotland': {
    label: 'Designated sites',
    sourceLinkLabel: 'protected sites in Scotland',
    accordionSummary:
      'Sites of Special Scientific Interest (SSSIs), Special Areas of Conservation (SACs) and Special Protection Areas (SPAs) in Scotland. Data: NatureScot.',
    sourceLink:
      'https://www.nature.scot/professional-advice/protected-areas-and-species/protected-areas',
    swatches: [
      {
        colour: '#E31A1C',
        label: 'SSSI boundary',
        type: 'hatch',
        hatch: 'cross',
      },
      {
        colour: '#0021F6',
        label: 'SAC boundary',
        type: 'hatch',
        hatch: 'vertical',
      },
      {
        colour: '#AD00B0',
        label: 'SPA boundary',
        type: 'hatch',
        hatch: 'horizontal',
      },
    ],
  },
  'alc-provisional-england': {
    // 'Land classification' (ALC) and 'Land capability' (LCA) confirmed
    // 11/09/2026: each nation keeps its own scheme's name.
    //
    // The destination was queried on 11/09/2026 and KEPT. It is framed at
    // developers and planning authorities, which is wrong for a farmer, but the
    // page now carries an "About ALC grades" section covering Grade 1 to Grade 5
    // including 3a and 3b in its own body. The alternatives are worse: Natural
    // England's TIN049 explainer is only served over an expiring signed download
    // link, and the JP069 grading manual is a surveyor's document.
    label: 'Land classification',
    sourceLinkLabel: 'land classification',
    accordionSummary:
      "The Agricultural Land Classification (ALC) grades England's farmland on its quality and versatility for growing crops. Grade 1-2 is the most flexible and productive; Grade 4-5 is limited to rough grazing. Data: Environment Agency / Natural England (OGL).",
    sourceLink:
      'https://www.gov.uk/government/publications/agricultural-land-assess-proposals-for-development/guide-to-assessing-development-proposals-on-agricultural-land',
    swatches: [
      { colour: '#37E2FE', label: 'Grade 1 - excellent' },
      { colour: '#C2FBFE', label: 'Grade 2 - very good' },
      { colour: '#B2FE99', label: 'Grade 3 - good to moderate' },
      { colour: '#FEF8A4', label: 'Grade 4 - poor' },
      { colour: '#B28864', label: 'Grade 5 - very poor' },
      { colour: '#FF6355', label: 'Urban' },
    ],
  },
  'alc2-predictive-wales': {
    // 'Land classification' (ALC) and 'Land capability' (LCA) confirmed
    // 11/09/2026: each nation keeps its own scheme's name.
    label: 'Land classification',
    sourceLinkLabel: 'land classification',
    accordionSummary:
      'Predictive Agricultural Land Classification for Wales, assessing land quality and versatility. Data: Welsh Government / Natural Resources Wales.',
    sourceLink: 'https://www.gov.wales/agricultural-land-classification',
    // Grades pinned to inspire-wg:wg_predictive_alc2 legend (3a/3b split; no standalone Grade 3).
    swatches: [
      { colour: '#0081FE', label: 'Grade 1' },
      { colour: '#C2FBFE', label: 'Grade 2' },
      { colour: '#018100', label: 'Grade 3a' },
      { colour: '#98FB98', label: 'Grade 3b' },
      { colour: '#FEFB69', label: 'Grade 4' },
      { colour: '#B28864', label: 'Grade 5' },
      { colour: '#FEC455', label: 'Non-agricultural' },
      { colour: '#FF6355', label: 'Urban' },
    ],
  },
  // ONE ENTRY, replacing the identical 'hutton-lca-50k-scotland' and
  // 'hutton-lca-250k-scotland' pair, now that the two Scottish LCA sources are
  // one Hybrid cache (see data/categories/land-capability.js, 18/08/2026).
  //
  // KNOWN GAP, pre-existing and deliberately not fixed here: Hutton subdivide
  // classes 3 to 6 into 3.1/3.2, 4.1/4.2, 5.1/5.2/5.3 and 6.1/6.2/6.3, each
  // with its own colour, so eight swatches describe fifteen rendered classes.
  // That was equally true of the old druid services, which served the same
  // dataset, so it is not a regression. Expanding the legend is a design change
  // rather than a fix.
  'hutton-lca-scotland': {
    // 'Land classification' (ALC) and 'Land capability' (LCA) confirmed
    // 11/09/2026: each nation keeps its own scheme's name.
    label: 'Land capability',
    sourceLinkLabel: 'land capability',
    accordionSummary:
      "Land Capability for Agriculture (LCA) classifies Scotland's farmland by what crops, grass or grazing each area can support. Class 1 is the most flexible arable land; Class 7 is very limited. Shown at 1:50,000 where that survey exists, and 1:250,000 elsewhere. Data: James Hutton Institute.",
    // Was the capability map viewer on the soils portal, i.e. the data. This is
    // the James Hutton Institute's own explanation of the classification, from
    // the body that originated it, and it says what Class 1 through Class 7
    // mean. It also avoids soils.environment.gov.scot, whose portal terms are
    // not Hutton's own. Verified 200 on 11/09/2026.
    sourceLink: 'https://www.hutton.ac.uk/land-capability-for-agriculture-lca/',
    // COLOURS RE-READ FROM THE SERVICE, 18/08/2026, not carried over. These come
    // from the chosen cache's own legend endpoint, so the key matches what is
    // drawn. The previous values were druid's palette and would now all be
    // wrong. Same selection rule as before: where Hutton subdivide a class, the
    // swatch shows the first sub-class, so Class 3 is 3.1 rather than an average
    // of 3.1 and 3.2.
    swatches: [
      { colour: '#BF6300', label: 'Class 1 - best arable' },
      { colour: '#FFFF00', label: 'Class 2 - very good arable' },
      { colour: '#C2FFC2', label: 'Class 3 - good to average arable' },
      { colour: '#27B1C4', label: 'Class 4 - mostly grass' },
      { colour: '#F226FF', label: 'Class 5 - improved grassland' },
      { colour: '#AB7FD4', label: 'Class 6 - rough grazing' },
      { colour: '#E0E0E0', label: 'Class 7 - very limited agricultural value' },
      { colour: '#A1A1A1', label: 'Built-up / urban' },
    ],
  },
};
